import type { NostrEvent } from "@/nostr/types"

/**
 * People axis of the unified filter architecture (authors and following).
 *
 * An author *include* list can be pushed down to the relay as `authors`, which
 * keeps pagination useful. An *exclude* list (and the following / non-following
 * modes, which depend on the reader's kind 3 list) can only be applied client
 * side because NIP-01 cannot express them.
 */
export type AuthorMode = "include" | "exclude"

export const AUTHOR_MODES: readonly AuthorMode[] = ["include", "exclude"]

export type FollowingMode = "everyone" | "following" | "non-following"

export const FOLLOWING_MODES: readonly FollowingMode[] = ["everyone", "following", "non-following"]

export interface AuthorFilterState {
  /** Public keys selected in the author picker. */
  authors: string[]
  authorMode: AuthorMode
  following: FollowingMode
}

/** Include / exclude matching. */
export function matchesAuthors(event: NostrEvent, state: AuthorFilterState): boolean {
  if (state.authors.length === 0) return true
  const selected = state.authors.includes(event.pubkey)
  return state.authorMode === "exclude" ? !selected : selected
}

/**
 * Following status matching. Without a follow list the mode cannot be applied,
 * so it passes everything instead of hiding the whole feed.
 */
export function matchesFollowing(
  event: NostrEvent,
  mode: FollowingMode,
  followingSet?: Set<string>,
): boolean {
  if (mode === "everyone" || !followingSet || followingSet.size === 0) return true
  const isFollowing = followingSet.has(event.pubkey)
  return mode === "following" ? isFollowing : !isFollowing
}

/** The `authors` field for the relay subscription, when it is safe to send it. */
export function relayAuthorsFor(state: AuthorFilterState): string[] | undefined {
  if (state.authorMode !== "include" || state.authors.length === 0) return undefined
  return state.authors
}

/** Trims and de-duplicates a set of public keys. */
export function uniquePubkeys(pubkeys: readonly string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const pubkey of pubkeys) {
    const value = pubkey.trim().toLowerCase()
    if (!/^[0-9a-f]{64}$/.test(value) || seen.has(value)) continue
    seen.add(value)
    result.push(value)
  }
  return result
}
